import { useTranslation } from "react-i18next";
import { pricingPlans, pricingFeatureKeys } from "@/mocks/siteData";
import { waLink } from "@/lib/links";
import SectionHeading from "@/components/feature/SectionHeading";

export default function PricingSection() {
  const { t } = useTranslation();

  return (
    <section id="pricing" className="py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionHeading
          eyebrow={t("pricing.eyebrow")}
          title={
            <>
              {t("pricing.title1")}
              <br />
              {t("pricing.title2")}
            </>
          }
          subtitle={t("pricing.subtitle")}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {pricingPlans.map((plan) => {
            const planName = t(`pricing.plans.${plan.id}.name`);
            return (
              <div
                key={plan.id}
                className={`relative rounded-lg p-8 flex flex-col transition ${
                  plan.featured
                    ? "bg-foreground-900 text-background-50 border-2 border-primary-500 md:scale-[1.03]"
                    : "bg-background-50 border border-background-200 hover:border-primary-300"
                }`}
              >
                <span
                  className={`absolute -top-3 left-8 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${
                    plan.featured ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-800"
                  }`}
                >
                  {t(`pricing.plans.${plan.id}.tag`)}
                </span>

                <div
                  className={`w-14 h-14 rounded-lg flex items-center justify-center ${
                    plan.featured ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700"
                  }`}
                >
                  <i className={`${plan.icon} text-2xl w-7 h-7 flex items-center justify-center`} />
                </div>

                <h3
                  className={`mt-6 font-heading text-2xl font-bold ${
                    plan.featured ? "text-background-50" : "text-foreground-900"
                  }`}
                >
                  {planName}
                </h3>

                <div className="mt-6 flex items-baseline gap-2">
                  <span
                    className={`font-heading font-extrabold text-4xl md:text-5xl ${
                      plan.featured ? "text-background-50" : "text-foreground-900"
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      plan.featured ? "text-background-50/70" : "text-foreground-500"
                    }`}
                  >
                    {t(`pricing.plans.${plan.id}.unit`)}
                  </span>
                </div>

                <ul className="mt-8 space-y-3 flex-1">
                  {pricingFeatureKeys.map((featureKey) => (
                    <li
                      key={featureKey}
                      className={`flex items-start gap-3 text-sm ${
                        plan.featured ? "text-background-50/85" : "text-foreground-600"
                      }`}
                    >
                      <span
                        className={`w-5 h-5 mt-0.5 rounded-full flex items-center justify-center shrink-0 ${
                          plan.featured ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700"
                        }`}
                      >
                        <i className="ri-check-line text-sm w-4 h-4 flex items-center justify-center" />
                      </span>
                      {t(`pricing.plans.${plan.id}.${featureKey}`)}
                    </li>
                  ))}
                </ul>

                <a
                  href={waLink(t("wa.pricing", { plan: planName }))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-10 w-full inline-flex items-center justify-center gap-2 font-semibold text-sm px-5 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition ${
                    plan.featured
                      ? "bg-primary-500 hover:bg-primary-600 text-background-50"
                      : "bg-foreground-900 hover:bg-primary-500 text-background-50"
                  }`}
                >
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                  {t(`pricing.plans.${plan.id}.cta`)}
                </a>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-foreground-400 text-sm">{t("pricing.note")}</p>
      </div>
    </section>
  );
}